import React from 'react';
import './HeatmapControls.css';

interface HeatmapControlsProps {
  dataType: 'jobs' | 'salaries' | 'companies';
  onDataTypeChange: (type: 'jobs' | 'salaries' | 'companies') => void;
  currentBounds: { zoom: number } | null;
  dataPointsCount: number;
  darkMode: boolean;
  showCompanies: boolean;
  onToggleCompanies: (show: boolean) => void;
  companyCount?: number;
  macroDataCount?: number;
}

const HeatmapControls: React.FC<HeatmapControlsProps> = ({
  dataType,
  onDataTypeChange,
  currentBounds,
  dataPointsCount,
  darkMode,
  showCompanies,
  onToggleCompanies,
  companyCount = 0,
  macroDataCount = 0
}) => {
  const [isMinimized, setIsMinimized] = React.useState(true);
  
  const dataTypes = [
    { value: 'salaries', label: 'Salary Levels', icon: 'attach_money', color: '#1E88E5' },
    { value: 'jobs', label: 'Job Density', icon: 'work', color: '#1976D2' },
    { value: 'companies', label: 'Company Density', icon: 'business', color: '#0D47A1' }
  ];

  const currentIndex = dataTypes.findIndex(type => type.value === dataType);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(e.target.value);
    onDataTypeChange(dataTypes[index].value as 'jobs' | 'salaries' | 'companies');
  };

  if (isMinimized) {
    return (
      <div className={`heatmap-controls-modern minimized ${darkMode ? 'dark' : ''}`}>
        <button 
          className="expand-btn"
          onClick={() => setIsMinimized(false)}
          title="Expand controls"
        >
          <span className="material-icons">{dataTypes[currentIndex].icon}</span>
          <span className="label">Heatmap</span>
          <span className="expand-icon">◀</span>
        </button>
      </div>
    );
  }

  return (
    <div className={`heatmap-controls-modern ${darkMode ? 'dark' : ''}`}>
      <div className="controls-header">
        <div>
          <h3>Heatmap Data</h3>
          <div className="data-stats">
            {currentBounds && (
              <span className="zoom-indicator">
                <span className="material-icons">search</span>
                {Math.round(currentBounds.zoom)}
              </span>
            )}
            <span className="points-indicator">
              <span className="material-icons">location_on</span>
              {dataPointsCount.toLocaleString()}
            </span>
          </div>
        </div>
        <button 
          className="minimize-btn"
          onClick={() => setIsMinimized(true)}
          title="Minimize controls"
        >
          ▶
        </button>
      </div>

      <div className="data-type-selector">
        <div className="slider-container">
          <input
            type="range"
            min="0"
            max="2"
            value={currentIndex}
            onChange={handleSliderChange}
            className="data-type-slider"
            style={{
              background: `linear-gradient(to right, 
                ${dataTypes[0].color} 0%, 
                ${dataTypes[0].color} 33%, 
                ${dataTypes[1].color} 33%, 
                ${dataTypes[1].color} 66%, 
                ${dataTypes[2].color} 66%, 
                ${dataTypes[2].color} 100%)`
            }}
          />
          <div className="slider-labels">
            {dataTypes.map((type, index) => (
              <div
                key={type.value}
                className={`slider-label ${currentIndex === index ? 'active' : ''}`}
                onClick={() => onDataTypeChange(type.value as any)}
              >
                <span className="material-icons">{type.icon}</span>
                <span className="label-text">{type.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="current-selection">
          <div className="selection-icon" style={{ backgroundColor: dataTypes[currentIndex].color }}>
            <span className="material-icons">{dataTypes[currentIndex].icon}</span>
          </div>
          <div className="selection-details">
            <h4>{dataTypes[currentIndex].label}</h4>
            <p className="selection-description">
              {dataType === 'salaries' && 'Average salary ranges across regions'}
              {dataType === 'jobs' && 'Number of job opportunities per area'}
              {dataType === 'companies' && 'Company concentration by location'}
            </p>
          </div>
        </div>
      </div>

      <div className="legend-compact">
        <div className="intensity-bar" style={{
          background: `linear-gradient(to right, 
            ${dataTypes[currentIndex].color}20, 
            ${dataTypes[currentIndex].color}80, 
            ${dataTypes[currentIndex].color})`
        }}></div>
        <div className="intensity-labels">
          <span>Low</span>
          <span>High</span>
        </div>
      </div>

      <div className="company-toggle-compact">
        <label className="toggle-row">
          <span className="toggle-label">
            <span className="material-icons">business</span> Companies ({companyCount})
          </span>
          <input
            type="checkbox"
            checked={showCompanies}
            onChange={(e) => onToggleCompanies(e.target.checked)}
            className="toggle-checkbox"
          />
        </label>
      </div>
    </div>
  );
};

export default HeatmapControls;